import type {PatternCode, SignalDescription, TrendPatternCategory, ZoneType} from '../types';

const COMMON_RISK_TIP = '该信号仅反映特定时间窗口内的价格行为，不能单独作为确定性交易依据；应结合趋势、成交量、关键区域和风险承受能力判断。';

export const PATTERN_DESCRIPTIONS: Partial<Record<PatternCode, SignalDescription>> = {
    HAMMER: {
        definition: '小实体位于上方，下影线明显长于实体',
        meaning: '下跌后低位买盘承接增强，可能出现底部反转',
        confirmation: '后续收盘站上锤子线高点并伴随放量',
        invalidation: '收盘有效跌破锤子线低点',
        riskTip: COMMON_RISK_TIP,
    },
    INVERTED_HAMMER: {
        definition: '小实体位于下方，上影线明显长于实体',
        meaning: '下跌中买方开始向上试探，但仍需确认',
        confirmation: '后续阳线收盘突破形态高点',
        invalidation: '价格继续创新低并跌破形态低点',
        riskTip: COMMON_RISK_TIP,
    },
    SHOOTING_STAR: {
        definition: '小实体位于下方，上影线明显长于实体，通常出现在上涨后',
        meaning: '高位上攻受阻，卖压增强，可能出现顶部反转',
        confirmation: '后续阴线收盘跌破形态低点',
        invalidation: '价格重新突破并站稳形态高点',
        riskTip: COMMON_RISK_TIP,
    },
    DOJI: {
        definition: '开盘价与收盘价接近，上下影线体现双向波动',
        meaning: '多空暂时均衡，原趋势可能停顿但方向未确认',
        confirmation: '后续K线有效突破十字星高点或低点',
        invalidation: '价格继续在十字星范围内震荡，未形成方向突破',
        riskTip: COMMON_RISK_TIP,
    },
    MARUBOZU_GREEN: {
        definition: '大阳线实体明显，影线很短或不存在',
        meaning: '买方持续主导，短期上涨动能较强',
        confirmation: '后续价格守住实体中上部并继续放量上行',
        invalidation: '收盘快速跌回实体中点下方',
        riskTip: COMMON_RISK_TIP,
    },
    MARUBOZU_RED: {
        definition: '大阴线实体明显，影线很短或不存在',
        meaning: '卖方持续主导，短期下跌动能较强',
        confirmation: '后续价格受压于实体中下部并继续下行',
        invalidation: '收盘快速反弹至实体中点上方',
        riskTip: COMMON_RISK_TIP,
    },
    ENGULFING_BULL: {
        definition: '当前阳线实体完整覆盖前一根阴线实体',
        meaning: '买方力量反转并压过前一周期卖方',
        confirmation: '后续站稳吞没形态高点并伴随成交量增加',
        invalidation: '价格重新跌破吞没形态低点',
        riskTip: COMMON_RISK_TIP,
    },
    ENGULFING_BEAR: {
        definition: '当前阴线实体完整覆盖前一根阳线实体',
        meaning: '卖方力量反转并压过前一周期买方',
        confirmation: '后续跌破吞没形态低点并伴随成交量增加',
        invalidation: '价格重新突破吞没形态高点',
        riskTip: COMMON_RISK_TIP,
    },
    HARAMI_BULL: {
        definition: '当前小阳线实体位于前一根大阴线实体内部',
        meaning: '下跌动能收缩，市场可能由空转稳',
        confirmation: '后续突破母线高点或关键阻力',
        invalidation: '价格跌破母线低点并延续下跌',
        riskTip: COMMON_RISK_TIP,
    },
    HARAMI_BEAR: {
        definition: '当前小阴线实体位于前一根大阳线实体内部',
        meaning: '上涨动能收缩，市场可能由多转弱',
        confirmation: '后续跌破母线低点或关键支撑',
        invalidation: '价格突破母线高点并延续上涨',
        riskTip: COMMON_RISK_TIP,
    },
    MORNING_STAR: {
        definition: '大阴线、弱势小实体、强势阳线组成三K线结构',
        meaning: '下跌动能衰竭后买方接管，属于底部反转组合',
        confirmation: '第三根阳线深入第一根阴线实体且后续站稳',
        invalidation: '价格跌破组合最低点',
        riskTip: COMMON_RISK_TIP,
    },
    EVENING_STAR: {
        definition: '大阳线、弱势小实体、强势阴线组成三K线结构',
        meaning: '上涨动能衰竭后卖方接管，属于顶部反转组合',
        confirmation: '第三根阴线深入第一根阳线实体且后续受压',
        invalidation: '价格突破组合最高点',
        riskTip: COMMON_RISK_TIP,
    },
    THREE_WHITE_SOLDIERS: {
        definition: '连续三根实体较强的阳线，收盘价逐步抬高',
        meaning: '买方连续推进，可能确认上升趋势',
        confirmation: '后续回踩不破第三根或组合关键支撑',
        invalidation: '出现强阴线吞没组合主体并跌破关键低点',
        riskTip: COMMON_RISK_TIP,
    },
    THREE_BLACK_CROWS: {
        definition: '连续三根实体较强的阴线，收盘价逐步降低',
        meaning: '卖方连续推进，可能确认下降趋势',
        confirmation: '后续反弹受阻并继续跌破组合低点',
        invalidation: '出现强阳线吞没组合主体并突破关键高点',
        riskTip: COMMON_RISK_TIP,
    },
};

export const ZONE_DESCRIPTIONS: Record<ZoneType, SignalDescription> = {
    LOW: {
        definition: '多次低点聚集形成的价格承接区',
        meaning: '历史买盘在此区域较活跃，回落时可能获得支撑',
        confirmation: '价格进入区域后止跌，并重新站上核心价格或上界',
        invalidation: '第一根已完成K线收盘价严格低于下界',
        riskTip: COMMON_RISK_TIP,
    },
    HIGH: {
        definition: '多次高点聚集形成的价格压力区',
        meaning: '历史卖盘在此区域较活跃，上涨时可能受到压制',
        confirmation: '价格进入区域后受阻，并重新跌破核心价格或下界',
        invalidation: '第一根已完成K线收盘价严格高于上界',
        riskTip: COMMON_RISK_TIP,
    },
    RANGE: {
        definition: '价格在明确上下边界之间反复波动形成箱体',
        meaning: '多空力量相对均衡，突破方向可能决定下一阶段走势',
        confirmation: '第一根已完成K线收盘价严格突破任一边界',
        invalidation: '突破后下一根K线快速收回箱体，形成假突破',
        riskTip: COMMON_RISK_TIP,
    },
};

// 趋势形态描述
export const TREND_DESCRIPTIONS: Record<string, SignalDescription> = {
    HEAD_SHOULDERS_TOP: {
        definition: '左肩、头、右肩三个波峰构成，头部最高，两肩相近',
        meaning: '上涨趋势终结概率增加，可能转为下跌',
        confirmation: '收盘价有效跌破颈线（两肩低点连线）',
        invalidation: '价格重新突破颈线并站稳',
        riskTip: COMMON_RISK_TIP,
    },
    HEAD_SHOULDERS_BOTTOM: {
        definition: '左肩、头、右肩三个波谷构成，头部最低，两肩相近',
        meaning: '下跌趋势终结概率增加，可能转为上涨',
        confirmation: '收盘价有效突破颈线（两肩高点连线）',
        invalidation: '价格重新跌破颈线',
        riskTip: COMMON_RISK_TIP,
    },
    DOUBLE_TOP: {
        definition: '两个相近高度的高点之间有明显的回调',
        meaning: '两次上攻失败，趋势可能由多转空',
        confirmation: '收盘价有效跌破两峰之间的谷底支撑',
        invalidation: '价格突破双顶高点',
        riskTip: COMMON_RISK_TIP,
    },
    DOUBLE_BOTTOM: {
        definition: '两个相近高度的谷底之间有明显的反弹',
        meaning: '两次下探成功，趋势可能由空转多',
        confirmation: '收盘价有效突破两谷之间的峰值',
        invalidation: '价格跌破双底低点',
        riskTip: COMMON_RISK_TIP,
    },
    FLAG_BULL: {
        definition: '旗杆（快速上涨）+ 旗面（窄幅整理向下倾斜）',
        meaning: '上涨中继形态，整理后继续上涨概率高',
        confirmation: '收盘价突破旗面高点并伴随放量',
        invalidation: '价格跌破旗面下沿支撑',
        riskTip: COMMON_RISK_TIP,
    },
    FLAG_BEAR: {
        definition: '旗杆（快速下跌）+ 旗面（窄幅整理向上倾斜）',
        meaning: '下跌中继形态，整理后继续下跌概率高',
        confirmation: '收盘价跌破旗面低点并伴随放量',
        invalidation: '价格突破旗面上沿压力',
        riskTip: COMMON_RISK_TIP,
    },
    TRIANGLE: {
        definition: '上下轨收敛，价格波动逐渐收窄',
        meaning: '蓄势整理，突破方向决定后续走势',
        confirmation: '收盘价突破上轨（看多）或下轨（看空）',
        invalidation: '突破后快速收回，形成假突破',
        riskTip: COMMON_RISK_TIP,
    },
    TRIANGLE_SYMMETRIC: {
        definition: '高低点同时收敛，形成对称三角',
        meaning: '蓄势整理，突破方向决定后续走势，属双边形态',
        confirmation: '收盘价突破上轨（看多）或下轨（看空）',
        invalidation: '突破后快速收回，形成假突破',
        riskTip: COMMON_RISK_TIP,
    },
    TRIANGLE_ASCENDING: {
        definition: '高点持平，低点抬升，形成上升三角',
        meaning: '买方力量渐强，向上突破概率高，属持续形态',
        confirmation: '收盘价突破水平上轨',
        invalidation: '价格跌破下轨支撑',
        riskTip: COMMON_RISK_TIP,
    },
    TRIANGLE_DESCENDING: {
        definition: '低点持平，高点降低，形成下降三角',
        meaning: '卖方力量渐强，向下突破概率高，属持续形态',
        confirmation: '收盘价突破水平下轨',
        invalidation: '价格升穿上轨压力',
        riskTip: COMMON_RISK_TIP,
    },
    CUP_HANDLE: {
        definition: '碗状整理 + 杯柄回调',
        meaning: '中长线反转或持续形态，看涨概率较高',
        confirmation: '收盘价突破杯柄高点',
        invalidation: '价格跌破杯柄低点',
        riskTip: COMMON_RISK_TIP,
    },
    TRIPLE_TOP: {
        definition: '三个相近高度的顶峰，中间有两次回调',
        meaning: '三次上攻受阻，顶部反转概率高',
        confirmation: '收盘有效跌破颈线（两谷连线）',
        invalidation: '价格突破三顶最高点',
        riskTip: COMMON_RISK_TIP,
    },
    TRIPLE_BOTTOM: {
        definition: '三个相近深度的谷底，中间有两次反弹',
        meaning: '三次下探支撑有效，底部反转概率高',
        confirmation: '收盘有效突破颈线（两峰连线）',
        invalidation: '价格跌破三底最低点',
        riskTip: COMMON_RISK_TIP,
    },
    ROUNDING_TOP: {
        definition: '高点的上升斜率逐渐趋缓并转为下降，呈圆弧状',
        meaning: '上涨动能持续衰减，顶部反转概率增加',
        confirmation: '收盘跌破圆弧右侧支撑',
        invalidation: '价格重新突破圆弧高点',
        riskTip: COMMON_RISK_TIP,
    },
    ROUNDING_BOTTOM: {
        definition: '低点的下降斜率逐渐趋缓并转为上升，呈圆弧状',
        meaning: '下跌动能持续衰减，底部反转概率增加',
        confirmation: '收盘突破圆弧右侧阻力',
        invalidation: '价格重新跌破圆弧低点',
        riskTip: COMMON_RISK_TIP,
    },
    V_TOP: {
        definition: '快速冲高后立即急速反转下跌，呈V形',
        meaning: '顶部反转极为迅速，追高风险大',
        confirmation: '收盘跌破起始价位超过50%',
        invalidation: '价格突破V形高点延续涨势',
        riskTip: COMMON_RISK_TIP,
    },
    V_BOTTOM: {
        definition: '快速下跌后立即急速反弹上涨，呈V形',
        meaning: '底部反转极为迅速，抄底机会但需确认',
        confirmation: '收盘突破起始价位超过50%',
        invalidation: '价格跌破V形低点延续跌势',
        riskTip: COMMON_RISK_TIP,
    },
    PENNANT_BULL: {
        definition: '旗杆上涨 + 旗形收敛整理（三角旗面）',
        meaning: '上涨中继形态，整理后继续上涨概率高',
        confirmation: '收盘突破整理区间高点并放量',
        invalidation: '价格跌破整理区间下沿',
        riskTip: COMMON_RISK_TIP,
    },
    PENNANT_BEAR: {
        definition: '旗杆下跌 + 旗形收敛整理（三角旗面）',
        meaning: '下跌中继形态，整理后继续下跌概率高',
        confirmation: '收盘跌破整理区间低点并放量',
        invalidation: '价格突破整理区间上沿',
        riskTip: COMMON_RISK_TIP,
    },
    RECTANGLE_BULL: {
        definition: '价格在明显水平上下界之间矩形整理',
        meaning: '蓄势充分，向上突破后空间可期',
        confirmation: '收盘突破矩形上界',
        invalidation: '价格跌破矩形下界',
        riskTip: COMMON_RISK_TIP,
    },
    RECTANGLE_BEAR: {
        definition: '价格在明显水平上下界之间矩形整理',
        meaning: '蓄势充分，向下突破后空间可期',
        confirmation: '收盘跌破矩形下界',
        invalidation: '价格突破矩形上界',
        riskTip: COMMON_RISK_TIP,
    },
    BROADENING_TRIANGLE_BULL: {
        definition: '高点越来越高、低点越来越低，呈喇叭形扩散',
        meaning: '市场情绪分歧加大，向上突破确认方向',
        confirmation: '收盘突破扩散三角形上沿',
        invalidation: '价格跌破扩散三角形下沿',
        riskTip: COMMON_RISK_TIP,
    },
    BROADENING_TRIANGLE_BEAR: {
        definition: '高点越来越高、低点越来越低，呈喇叭形扩散',
        meaning: '市场情绪分歧加大，向下突破确认方向',
        confirmation: '收盘跌破扩散三角形下沿',
        invalidation: '价格突破扩散三角形上沿',
        riskTip: COMMON_RISK_TIP,
    },
    DIAMOND_BULL: {
        definition: '先扩散再收敛的菱形整理',
        meaning: '蓄势充分，向上突破后趋势延续概率高',
        confirmation: '收盘突破菱形右边界',
        invalidation: '价格跌破菱形右边界',
        riskTip: COMMON_RISK_TIP,
    },
    DIAMOND_BEAR: {
        definition: '先扩散再收敛的菱形整理',
        meaning: '蓄势充分，向下突破后趋势延续概率高',
        confirmation: '收盘跌破菱形右边界',
        invalidation: '价格突破菱形右边界',
        riskTip: COMMON_RISK_TIP,
    },
    RISING_WEDGE_BEAR: {
        definition: '高点和低点均逐步抬高，但收敛形成上升楔形',
        meaning: '看跌双边形态，假突破概率较大',
        confirmation: '收盘跌破楔形下轨支撑',
        invalidation: '价格突破楔形上轨',
        riskTip: COMMON_RISK_TIP,
    },
    FALLING_WEDGE_BULL: {
        definition: '高点和低点均逐步降低，但收敛形成下降楔形',
        meaning: '看涨双边形态，酝酿向上突破',
        confirmation: '收盘突破楔形上轨压力',
        invalidation: '价格跌破楔形下轨',
        riskTip: COMMON_RISK_TIP,
    },
};

// 趋势形态 code → 分类映射
export const TREND_CODE_CATEGORY: Record<string, TrendPatternCategory> = {
    // 反转形态
    HEAD_SHOULDERS_TOP: 'REVERSAL',
    HEAD_SHOULDERS_BOTTOM: 'REVERSAL',
    DOUBLE_TOP: 'REVERSAL',
    DOUBLE_BOTTOM: 'REVERSAL',
    TRIPLE_TOP: 'REVERSAL',
    TRIPLE_BOTTOM: 'REVERSAL',
    ROUNDING_TOP: 'REVERSAL',
    ROUNDING_BOTTOM: 'REVERSAL',
    V_TOP: 'REVERSAL',
    V_BOTTOM: 'REVERSAL',
    // 持续形态
    FLAG_BULL: 'CONTINUATION',
    FLAG_BEAR: 'CONTINUATION',
    CUP_HANDLE: 'CONTINUATION',
    PENNANT_BULL: 'CONTINUATION',
    PENNANT_BEAR: 'CONTINUATION',
    RECTANGLE_BULL: 'CONTINUATION',
    RECTANGLE_BEAR: 'CONTINUATION',
    BROADENING_TRIANGLE_BULL: 'CONTINUATION',
    BROADENING_TRIANGLE_BEAR: 'CONTINUATION',
    DIAMOND_BULL: 'CONTINUATION',
    DIAMOND_BEAR: 'CONTINUATION',
    // 双边形态
    TRIANGLE: 'BILATERAL',
    TRIANGLE_SYMMETRIC: 'BILATERAL',
    TRIANGLE_ASCENDING: 'CONTINUATION',
    TRIANGLE_DESCENDING: 'CONTINUATION',
    RISING_WEDGE_BEAR: 'BILATERAL',
    FALLING_WEDGE_BULL: 'BILATERAL',
};

// 趋势分类通用描述
export const TREND_CATEGORY_DESCRIPTIONS: Record<TrendPatternCategory, SignalDescription> = {
    REVERSAL: {
        definition: '趋势从上涨转为下跌，或从下跌转为上涨',
        meaning: '原有趋势动能衰竭，反转概率增加',
        confirmation: '形态完成 + 突破关键价位 + 成交量配合',
        invalidation: '价格延续原趋势并突破形态边界',
        riskTip: COMMON_RISK_TIP,
    },
    CONTINUATION: {
        definition: '趋势短暂整理后延续原有方向',
        meaning: '中继形态，趋势恢复后仍有空间',
        confirmation: '形态完成 + 突破整理区间边界',
        invalidation: '价格反向突破并延续新趋势',
        riskTip: COMMON_RISK_TIP,
    },
    BILATERAL: {
        definition: '价格可能在两个方向运行',
        meaning: '多空均衡，需等待突破确认方向',
        confirmation: '收盘价突破收敛区间任一边界',
        invalidation: '突破后快速收回',
        riskTip: COMMON_RISK_TIP,
    },
};
